// src/components/Assessment/AssessmentEngine.jsx

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Progress,
} from '@chakra-ui/react';

// Import components
import QuestionRenderer from './QuestionRenderer';
import Results from './Results';

const AssessmentEngine = ({
  questions,
  assessmentService,
  getFallbackRecommendation,
  onViewDetails,
  onAssessmentComplete,
  customResultsComponent,
  onClose,
}) => {
  const emailRef = useRef();
  
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [recommendation, setRecommendation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const totalSteps = questions.length;

  useEffect(() => {
    // Focus email field when reaching contact step
    const contactStepIndex = questions.findIndex(q => q.type === 'contact');
    if (step === contactStepIndex && emailRef.current) {
      setTimeout(() => {
        emailRef.current?.focus();
      }, 100);
    }
  }, [step, questions]);

  const updateAnswer = (field, value) => {
    setAnswers(prev => ({ ...prev, [field]: value }));
  };

  const next = () => {
    if (step === totalSteps - 2) { // Last step before results
      handleSubmitAssessment();
    } else {
      setStep(prev => prev + 1);
    }
  };

  const prev = () => setStep(prev => Math.max(prev - 1, 0));

  const reset = () => {
    setStep(0);
    setAnswers({});
    setRecommendation(null);
  };

  const handleSubmitAssessment = async () => {
    setIsLoading(true);
    
    try {
      // Call the assessment service
      const aiRecommendation = await assessmentService.getRecommendation(answers, {
        name: answers.name,
        email: answers.email
      });

      setRecommendation(aiRecommendation);
      setStep(prev => prev + 1);
      
      if (onAssessmentComplete) {
        onAssessmentComplete(answers, aiRecommendation);
      }
      
    } catch (error) {
      console.error('Assessment submission error:', error);
      // Fallback to basic recommendation
      const fallbackRecommendation = getFallbackRecommendation(answers);
      setRecommendation(fallbackRecommendation);
      setStep(prev => prev + 1);
      
      if (onAssessmentComplete) {
        onAssessmentComplete(answers, fallbackRecommendation);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getQuestionByStep = (step) => questions[step];

  const currentQuestion = getQuestionByStep(step);
  const isLastStep = step === totalSteps - 1;

  // Use custom results component if provided
  const ResultsComponent = customResultsComponent || Results;

  return (
    <>
      {/* Progress Bar - Show for all steps except intro and results */}
      {step > 0 && !isLastStep && (
        <Box mb={6}>
          <Progress
            value={(step / (totalSteps - 1)) * 100}
            size="sm"
            colorScheme="green"
            borderRadius="full"
          />
        </Box>
      )}

      {/* Content */}
      {isLastStep ? (
        <ResultsComponent 
          recommendation={recommendation}
          onReset={reset}
          onViewDetails={onViewDetails}
          answers={answers}
          isLoading={isLoading}
          onClose={onClose}
        />
      ) : (
        <QuestionRenderer
          question={currentQuestion}
          answers={answers}
          updateAnswer={updateAnswer}
          onNext={next}
          onPrev={prev}
          currentStep={step}
          totalSteps={totalSteps}
          emailRef={emailRef}
        />
      )}
    </>
  );
};

export default AssessmentEngine;